import { z } from 'zod';

import { CURRENCIES } from '../constants/currency';
import { APP_LOCALES } from '../constants/languages';
import { BIDDER_TYPES, USER_ROLES } from '../constants/roles';

import { referralCodeSchema } from './referral';

export const emailSchema = z.email().trim().toLowerCase();

export const passwordSchema = z
  .string()
  .min(12, 'Minimum 12 caractères')
  .max(128, 'Maximum 128 caractères')
  .regex(/[a-z]/, 'Au moins une minuscule')
  .regex(/[A-Z]/, 'Au moins une majuscule')
  .regex(/\d/, 'Au moins un chiffre');

export const signUpSchema = z.object({
  email: emailSchema,
  password: passwordSchema,
  role: z.enum(USER_ROLES).default('REQUESTER'),
  bidderType: z.enum(BIDDER_TYPES).optional(),
  currency: z.enum(CURRENCIES).default('EUR'),
  locale: z.enum(APP_LOCALES).default('fr'),
  acceptTerms: z.literal(true, {
    error: 'Vous devez accepter les CGU',
  }),
  acceptAi: z.literal(true, {
    error: "Vous devez accepter l'utilisation de l'IA",
  }),
  marketingOptIn: z.boolean().default(false),
  refCode: referralCodeSchema.optional(),
});
export type SignUpInput = z.infer<typeof signUpSchema>;

export const signInSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Mot de passe requis'),
  totp: z
    .string()
    .regex(/^\d{6}$/)
    .optional(),
});
export type SignInInput = z.infer<typeof signInSchema>;

export const verifyEmailSchema = z.object({
  token: z.string().min(16).max(256),
});
export type VerifyEmailInput = z.infer<typeof verifyEmailSchema>;

export const requestPasswordResetSchema = z.object({
  email: emailSchema,
});
export type RequestPasswordResetInput = z.infer<typeof requestPasswordResetSchema>;

export const resendVerificationEmailSchema = z.object({
  email: emailSchema,
});
export type ResendVerificationEmailInput = z.infer<typeof resendVerificationEmailSchema>;

export const resetPasswordSchema = z.object({
  token: z.string().min(16).max(256),
  password: passwordSchema,
});
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;

export const totpCodeSchema = z.string().regex(/^\d{6}$/, 'Code à 6 chiffres');

export const confirmTotpEnrollmentSchema = z.object({
  code: totpCodeSchema,
});
export type ConfirmTotpEnrollmentInput = z.infer<typeof confirmTotpEnrollmentSchema>;

export const disableTotpSchema = z.object({
  password: z.string().min(1, 'Mot de passe requis'),
});
export type DisableTotpInput = z.infer<typeof disableTotpSchema>;

export const completeOAuthSignupSchema = z.object({
  role: z.enum(USER_ROLES).default('REQUESTER'),
  bidderType: z.enum(BIDDER_TYPES).optional(),
  currency: z.enum(CURRENCIES).default('EUR'),
  locale: z.enum(APP_LOCALES).default('fr'),
  acceptTerms: z.literal(true, {
    error: 'Vous devez accepter les CGU',
  }),
  acceptAi: z.literal(true, {
    error: "Vous devez accepter l'utilisation de l'IA",
  }),
  marketingOptIn: z.boolean().default(false),
  refCode: referralCodeSchema.optional(),
});
export type CompleteOAuthSignupInput = z.infer<typeof completeOAuthSignupSchema>;

export const activateSecondRoleSchema = z.object({
  role: z.enum(USER_ROLES),
  bidderType: z.enum(BIDDER_TYPES).optional(),
});
export type ActivateSecondRoleInput = z.infer<typeof activateSecondRoleSchema>;
